// src/pages/ManualRefillReminders.js
//
// For CRM-only customers (see gf/models.py::OrganizationSettings.crm_only)
// who invoice through an external/offline POS. Mirrors the invoice-driven
// RefillReminders.js page, but backed by ManualRefillReminder — staff
// enter the medication name and refill date directly since there's no
// Invoice/Prescription to derive it from.

import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { AutoComplete } from 'primereact/autocomplete';
import { format } from 'date-fns';
import GlobalTable from '../components/GlobalTable';
import { useLazyTable } from '../hooks/useLazyTable';
import axiosInstance from '../services/axiosInstance';
import { handleApiError, handleApiSuccess } from '../components/apiErrorHandler';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';

const emptyForm = {
  customer: null,        // holds the selected customer object from AutoComplete
  medication_name: '',
  refill_date: null,
  reminder_date: null,
  notes: '',
};

const ManualRefillReminders = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const { lazyParams, onPage, onSort, onFilter, queryParams } = useLazyTable({ rows: 15 });
  const [loading, setLoading] = useState(false);
  const { push, beginDrilldown } = useBreadcrumbs();
  const navigate = useNavigate();

  const [dialogVisible, setDialogVisible] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadData = () => {
    setLoading(true);
    axiosInstance.get('/api/manual-refill-reminders/', { params: queryParams })
      .then(res => {
        setData(res.data.results || []);
        setTotalRecords(res.data.count || 0);
      })
      .catch(handleApiError)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [queryParams]);

  // ── SMS send (per row) ──────────────────────────────────────────────────
  const [sendingSms, setSendingSms] = useState({});

  const handleSendSms = async (row) => {
    if (sendingSms[row.id]) return;
    setSendingSms(prev => ({ ...prev, [row.id]: true }));
    try {
      await axiosInstance.post(`/api/manual-refill-reminders/${row.id}/send-sms/`);
      handleApiSuccess(`SMS reminder sent to ${row.customer_name}.`);
      setData(prev =>
        prev.map(r =>
          r.id === row.id ? { ...r, sms_sent_at: new Date().toISOString(), status: 'Completed' } : r
        )
      );
    } catch (err) {
      handleApiError(err);
    } finally {
      setSendingSms(prev => ({ ...prev, [row.id]: false }));
    }
  };

  // ── Create dialog ────────────────────────────────────────────────────────
  const fetchCustomerSuggestions = (query) => {
    axiosInstance.get(`/api/customerq?query=${query}`)
      .then(res => {
        const processed = res.data.map(c => ({ ...c, displayLabel: `${c.first_name || ''} ${c.last_name}`.trim() }));
        setCustomerSuggestions(processed);
      })
      .catch(handleApiError);
  };

  const openCreateDialog = () => {
    setForm(emptyForm);
    setDialogVisible(true);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.customer?.id) {
      handleApiError({ Error: 'Please select a customer.' });
      return;
    }
    if (!form.medication_name.trim()) {
      handleApiError({ Error: 'Medication name is required.' });
      return;
    }
    if (!form.refill_date || !form.reminder_date) {
      handleApiError({ Error: 'Both refill date and reminder date are required.' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        customer: form.customer.id,
        medication_name: form.medication_name.trim(),
        refill_date: format(new Date(form.refill_date), 'yyyy-MM-dd'),
        reminder_date: format(new Date(form.reminder_date), 'yyyy-MM-dd'),
        notes: form.notes,
      };
      const res = await axiosInstance.post('/api/manual-refill-reminders/', payload);
      setData(prev => [res.data, ...prev]);
      handleApiSuccess('Refill reminder created.');
      setDialogVisible(false);
      setForm(emptyForm);
    } catch (err) {
      handleApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const statusTemplate = row => (
    <Tag
      value={row.status}
      severity={row.status === 'Completed' ? 'success' : row.status === 'Missed' ? 'danger' : 'warning'}
    />
  );

  const smsTemplate = row => {
    const alreadySent = !!row.sms_sent_at;
    const isLoading = !!sendingSms[row.id];
    const sentLabel = alreadySent
      ? `Sent ${new Date(row.sms_sent_at).toLocaleDateString()}`
      : 'Send SMS';

    return (
      <Button
        icon={alreadySent ? 'pi pi-check' : 'pi pi-send'}
        label={sentLabel}
        className={`p-button-sm ${alreadySent ? 'p-button-success p-button-outlined' : 'p-button-outlined'}`}
        disabled={alreadySent || isLoading}
        loading={isLoading}
        onClick={() => handleSendSms(row)}
        tooltip={
          alreadySent
            ? `SMS sent at ${new Date(row.sms_sent_at).toLocaleString()}`
            : 'Send SMS refill reminder to this customer'
        }
        tooltipOptions={{ position: 'top' }}
      />
    );
  };

  const columns = [
    {
      Header: 'Customer',
      accessor: 'customer_name',
      filtered: true,
      bodyTemplate: (row) => (
        <span
          style={{ color: '#2563eb', cursor: 'pointer' }}
          onClick={() => {
            beginDrilldown();
            navigate(`/customers/${row.customer}`);
            push({ label: `Manual Refill Reminders`, path: '/manual-refill-reminders' });
            push({ label: `Customer: ${row.customer_name}`, path: `/customers/${row.customer}` });
          }}
        >
          {row.customer_name}
        </span>
      )
    },
    { Header: 'Medication',     accessor: 'medication_name', filtered: true },
    { Header: 'Refill Date',    accessor: 'refill_date',   dataType: 'date' },
    { Header: 'Reminder Date',  accessor: 'reminder_date', dataType: 'date' },
    { Header: 'Status',         bodyTemplate: statusTemplate },
    { Header: 'SMS Reminder',   bodyTemplate: smsTemplate },
  ];

  return (
    <div className="containers">
      <GlobalTable
        columns={columns}
        data={data}
        loading={loading}
        tabletitle="Manual Refill Reminders"
        showEditColumn={false}
        lazy
        totalRecords={totalRecords}
        lazyParams={lazyParams}
        onPage={onPage}
        onSort={onSort}
        onFilter={onFilter}
        headerAction={
          <>
            <Button icon="pi pi-plus" label="New Reminder" onClick={openCreateDialog} className="mr-2" />
            <Button icon="pi pi-refresh" label="Refresh" outlined onClick={loadData} />
          </>
        }
      />

      <Dialog
        header="New Manual Refill Reminder"
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        modal
        style={{ width: '30rem', borderRadius: '12px' }}
        className="p-fluid"
      >
        <div style={{ padding: '0.5rem' }}>
          <div className="field">
            <label htmlFor="customer" className="font-bold">Customer:</label>
            <AutoComplete
              id="customer"
              value={form.customer}
              suggestions={customerSuggestions}
              completeMethod={(e) => fetchCustomerSuggestions(e.query)}
              field="displayLabel"
              onChange={(e) => handleFormChange('customer', e.value)}
              placeholder="Type in phone number or name"
            />
          </div>

          <div className="field">
            <label htmlFor="medication_name" className="font-bold">Medication Name:</label>
            <InputText
              id="medication_name"
              value={form.medication_name}
              onChange={(e) => handleFormChange('medication_name', e.target.value)}
              placeholder="e.g. Amlodipine 5mg"
            />
          </div>

          <div className="formgrid grid">
            <div className="field col">
              <label htmlFor="refill_date" className="font-bold">Refill Date:</label>
              <Calendar
                id="refill_date"
                value={form.refill_date}
                onChange={(e) => handleFormChange('refill_date', e.value)}
                showIcon
              />
            </div>
            <div className="field col">
              <label htmlFor="reminder_date" className="font-bold">Reminder Date:</label>
              <Calendar
                id="reminder_date"
                value={form.reminder_date}
                onChange={(e) => handleFormChange('reminder_date', e.value)}
                showIcon
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="notes" className="font-bold">Notes:</label>
            <InputTextarea
              id="notes"
              value={form.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              rows={3}
              placeholder="Optional notes"
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
            <Button label="Save" icon="pi pi-check" onClick={handleSave} loading={saving} style={{ maxWidth: '120px' }} />
            <Button label="Cancel" icon="pi pi-times" outlined onClick={() => setDialogVisible(false)} style={{ maxWidth: '120px' }} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ManualRefillReminders;